// src/pages/WholesaleCatalog.jsx

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import axiosService from "../utils/axios.ts";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useNavigate,
} from "react-router-dom";

import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

import {
    Helmet,
} from "react-helmet-async";

import Layout from "../components/layoutBlack";
// import Breadcrumbs from "../components/breadcrumbs";

import {
    addToCart,
} from "../store/slices/cartSlice.ts";

import {
    config,
} from "../utils/constants";


const REACT_APP_API_URL =
    config.url.REACT_APP_API_URL;

const REACT_API_IMAGE_URL =
    config.url.REACT_API_IMAGE_URL;


const WholesaleCatalog = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const accessToken = useSelector(
        (state) =>
            state.auth.accessToken
    );

    const account = useSelector(
        (state) =>
            state.auth.account
    );

    const isAuthenticated = useSelector(
        (state) =>
            state.auth.isAuthenticated
    );

    const [
        products,
        setProducts,
    ] = useState([]);

    const [
        quantities,
        setQuantities,
    ] = useState({});

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        errorMessage,
        setErrorMessage,
    ] = useState("");

    const isApprovedWholesale =
        account?.profile
            ?.wholesale_status ===
            "approved" &&
        account?.profile
            ?.is_wholesale === true;


    const getMinimumQuantity = (
        product
    ) => {
        const minimum = Number(
            product
                ?.wholesale_minimum_quantity
        );

        if (
            Number.isInteger(minimum) &&
            minimum > 0
        ) {
            return minimum;
        }

        return 6;
    };


    const asImageUrl = (
        image
    ) => {
        if (!image) {
            return "/default.jpg";
        }

        if (
            /^https?:\/\//i.test(
                image
            )
        ) {
            return image;
        }

        const base = String(
            REACT_API_IMAGE_URL || ""
        ).replace(
            /\/+$/,
            ""
        );

        const path = String(
            image
        ).replace(
            /^\/+/,
            ""
        );

        return `${base}/${path}`;
    };


    /*
     * Redirect users who are not authorized to view the
     * wholesale catalog.
     */
    useEffect(() => {
        if (!isAuthenticated) {
            navigate(
                "/auth/login",
                {
                    replace: true,
                }
            );

            return;
        }

        if (!isApprovedWholesale) {
            navigate(
                "/",
                {
                    replace: true,
                }
            );
        }
    }, [
        isAuthenticated,
        isApprovedWholesale,
        navigate,
    ]);


    /*
     * Load the wholesale products.
     */
    useEffect(() => {
        if (
            !isAuthenticated ||
            !isApprovedWholesale ||
            !accessToken
        ) {
            return;
        }

        let mounted = true;

        const loadProducts =
            async () => {
                try {
                    setLoading(true);
                    setErrorMessage("");

                    const response =
                        await axiosService.get(
                            `${REACT_APP_API_URL}/wholesale-catalog/`,
                            {
                                headers: {
                                    Authorization:
                                        `Bearer ${accessToken}`,
                                },
                                withCredentials:
                                    true,
                            }
                        );

                    if (!mounted) {
                        return;
                    }

                    const productData =
                        Array.isArray(
                            response.data
                        )
                            ? response.data
                            : response.data
                                  ?.results || [];

                    setProducts(
                        productData
                    );

                    const initialQuantities =
                        {};

                    productData.forEach(
                        (product) => {
                            initialQuantities[
                                product.id
                            ] =
                                getMinimumQuantity(
                                    product
                                );
                        }
                    );

                    setQuantities(
                        initialQuantities
                    );
                } catch (error) {
                    if (!mounted) {
                        return;
                    }

                    console.error(
                        "Unable to load wholesale products:",
                        error
                    );

                    if (
                        error.response
                            ?.status ===
                        401
                    ) {
                        setErrorMessage(
                            "Your login has expired. Please sign in again."
                        );

                        return;
                    }

                    if (
                        error.response
                            ?.status ===
                        403
                    ) {
                        setErrorMessage(
                            "An approved wholesale account is required to view this catalog."
                        );

                        return;
                    }

                    setErrorMessage(
                        error.response
                            ?.data
                            ?.detail ||
                            "The wholesale catalog could not be loaded."
                    );
                } finally {
                    if (mounted) {
                        setLoading(false);
                    }
                }
            };

        loadProducts();

        return () => {
            mounted = false;
        };
    }, [
        accessToken,
        isAuthenticated,
        isApprovedWholesale,
    ]);


    const wholesaleProductCount =
        products.length;


    const minimumOrderUnits =
        useMemo(
            () => 24,
            []
        );


    const getQuantity = (
        product
    ) => {
        const minimum =
            getMinimumQuantity(
                product
            );

        const current =
            Number(
                quantities[
                    product.id
                ]
            );

        if (
            !Number.isInteger(
                current
            ) ||
            current < minimum
        ) {
            return minimum;
        }

        return current;
    };


    const updateQuantity = (
        product,
        value
    ) => {
        const minimum =
            getMinimumQuantity(
                product
            );

        const numericValue =
            Number.parseInt(
                value,
                10
            );

        setQuantities(
            (current) => ({
                ...current,

                [product.id]:
                    Number.isInteger(
                        numericValue
                    )
                        ? Math.max(
                              numericValue,
                              minimum
                          )
                        : minimum,
            })
        );
    };


    const incrementQuantity = (
        product
    ) => {
        const current =
            getQuantity(
                product
            );

        setQuantities(
            (quantitiesState) => ({
                ...quantitiesState,

                [product.id]:
                    current + 1,
            })
        );
    };


    const decrementQuantity = (
        product
    ) => {
        const minimum =
            getMinimumQuantity(
                product
            );

        const current =
            getQuantity(
                product
            );

        setQuantities(
            (quantitiesState) => ({
                ...quantitiesState,

                [product.id]:
                    Math.max(
                        minimum,
                        current - 1
                    ),
            })
        );
    };


    const openProductDetail = (
        product
    ) => {
        /*
         * ProductDetail expects the family slug and selected
         * Product ID when navigating from another page.
         */
        const detailSlug =
            product.family_slug ||
            product.slug;

        if (!detailSlug) {
            return;
        }

        navigate(
            `/product/${detailSlug}`,
            {
                state: {
                    familySlug:
                        product.family_slug ||
                        detailSlug,

                    product_id:
                        product.id,
                },
            }
        );
    };


    const handleAddToCart = (
        product
    ) => {
        const quantity =
            getQuantity(
                product
            );

        const minimum =
            getMinimumQuantity(
                product
            );

        if (quantity < minimum) {
            Swal.fire({
                icon: "warning",

                text:
                    `The minimum wholesale quantity for ` +
                    `${product.name} is ${minimum} units.`,

                confirmButtonText:
                    "Close",
            });

            return;
        }

        const image =
            asImageUrl(
                product.display_image
            );

        dispatch(
            addToCart({
                id: product.id,
                sku: product.sku,
                name: product.name,

                /*
                 * This price is used for display in the
                 * frontend cart. Django must recalculate
                 * the actual price during checkout.
                 */
                price: Number(
                    product.wholesale_price ||
                    0
                ),

                unit_weight_oz:
                    product
                        .unit_weight_ounces ||
                    0,

                hs_code:
                    product.hs_code || "",

                image,

                tariff_number:
                    product
                        .tariff_number ||
                    "",

                customs_description:
                    product
                        .customs_description ||
                    "",

                customs_origin_country:
                    product
                        .customs_origin_country ||
                    "",

                customs_is_provisional:
                    product
                        .customs_is_provisional ||
                    false,

                quantity,

                option:
                    product.product_size ||
                    "",

                wholesale_minimum_quantity:
                    minimum,
            })
        );

        Swal.fire({
            icon: "success",

            html:
                `<p style="padding-bottom: 1rem; font-size: 1.1rem;">` +
                `"${product.name}" added to your wholesale cart.</p>` +
                `<p>${quantity} units added.</p>`,

            showCloseButton: true,
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,

            customClass: {
                popup:
                    "rounded shadow",
                title:
                    "text-center",
            },
        });
    };


    if (
        !isAuthenticated ||
        !isApprovedWholesale
    ) {
        return null;
    }


    return (
        <div>
            <Layout>
                <Helmet>
                    <title>
                        Wholesale Catalog | Molu
                        Wellness
                    </title>

                    <meta
                        name="robots"
                        content="noindex,nofollow"
                    />

                    <meta
                        name="description"
                        content="Molu Wellness wholesale product catalog."
                    />
                </Helmet>

                <section className="bg0 empty-top">
                    <div className="container p-t-10">
                        {/* <Breadcrumbs /> */}
                    </div>
                </section>

                <section className="full-page-section bg0 p-t-35 p-b-125">
                    <div className="container">
                        <div className="txt-center p-b-40">
                            <h1 className="ltext-105 cl2">
                                Wholesale Catalog
                            </h1>

                            <p className="stext-115 cl6 p-t-15">
                                Approved wholesale
                                pricing for Molu
                                Wellness partners.
                            </p>
                        </div>

                        <div className="row">
                            {/* Catalog table */}
                            <div className="col-lg-9">
                                {errorMessage ? (
                                    <div
                                        className="alert alert-danger"
                                        role="alert"
                                    >
                                        {
                                            errorMessage
                                        }
                                    </div>
                                ) : null}

                                {loading ? (
                                    <div className="bor10 p-5 txt-center">
                                        <p className="stext-115 cl6">
                                            Loading
                                            wholesale
                                            catalog...
                                        </p>
                                    </div>
                                ) : null}

                                {!loading &&
                                !errorMessage &&
                                wholesaleProductCount ===
                                    0 ? (
                                    <div className="bor10 p-5 txt-center">
                                        <p className="stext-115 cl6">
                                            There are
                                            currently no
                                            products
                                            available for
                                            wholesale
                                            ordering.
                                        </p>
                                    </div>
                                ) : null}

                                {!loading &&
                                products.length >
                                    0 ? (
                                    <div className="table-responsive bor10">
                                        <table className="table table-hover align-middle m-b-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th
                                                        scope="col"
                                                        style={{
                                                            minWidth:
                                                                "300px",
                                                        }}
                                                    >
                                                        Product
                                                    </th>

                                                    <th
                                                        scope="col"
                                                        className="text-nowrap"
                                                    >
                                                        SKU
                                                    </th>

                                                    <th
                                                        scope="col"
                                                        className="text-nowrap"
                                                    >
                                                        Wholesale
                                                        Price
                                                    </th>

                                                    <th
                                                        scope="col"
                                                        className="text-center text-nowrap"
                                                    >
                                                        SKU
                                                        Minimum
                                                    </th>

                                                    <th
                                                        scope="col"
                                                        className="text-center"
                                                        style={{
                                                            minWidth:
                                                                "150px",
                                                        }}
                                                    >
                                                        Quantity
                                                    </th>

                                                    <th
                                                        scope="col"
                                                        className="text-center"
                                                        style={{
                                                            minWidth:
                                                                "210px",
                                                        }}
                                                    >
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {products.map(
                                                    (
                                                        product
                                                    ) => {
                                                        const minimum =
                                                            getMinimumQuantity(
                                                                product
                                                            );

                                                        const quantity =
                                                            getQuantity(
                                                                product
                                                            );

                                                        return (
                                                            <tr
                                                                key={
                                                                    product.id
                                                                }
                                                            >
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-3">
                                                                        <img
                                                                            src={asImageUrl(
                                                                                product.display_image
                                                                            )}
                                                                            alt={
                                                                                product.name
                                                                            }
                                                                            loading="lazy"
                                                                            style={{
                                                                                width:
                                                                                    "80px",

                                                                                height:
                                                                                    "80px",

                                                                                objectFit:
                                                                                    "contain",

                                                                                flexShrink:
                                                                                    0,

                                                                                backgroundColor:
                                                                                    "#ffffff",

                                                                                border:
                                                                                    "1px solid #e5e5e5",

                                                                                borderRadius:
                                                                                    "4px",
                                                                            }}
                                                                        />

                                                                        <div>
                                                                            <div className="fw-bold text-dark">
                                                                                {
                                                                                    product.name
                                                                                }
                                                                            </div>

                                                                            {product.product_size ? (
                                                                                <div className="small text-muted p-t-5">
                                                                                    {
                                                                                        product.product_size
                                                                                    }
                                                                                </div>
                                                                            ) : null}

                                                                            {product.category_name ? (
                                                                                <div className="small text-muted p-t-3">
                                                                                    {
                                                                                        product.category_name
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                <td className="text-nowrap">
                                                                    {
                                                                        product.sku
                                                                    }
                                                                </td>

                                                                <td className="text-nowrap fw-bold">
                                                                    $
                                                                    {Number(
                                                                        product.wholesale_price ||
                                                                            0
                                                                    ).toFixed(
                                                                        2
                                                                    )}
                                                                </td>

                                                                <td className="text-center">
                                                                    {
                                                                        minimum
                                                                    }
                                                                </td>

                                                                <td>
                                                                    <div className="d-flex align-items-center justify-content-center">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-secondary btn-sm"
                                                                            aria-label={`Decrease quantity for ${product.name}`}
                                                                            onClick={() =>
                                                                                decrementQuantity(
                                                                                    product
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                quantity <=
                                                                                minimum
                                                                            }
                                                                        >
                                                                            −
                                                                        </button>

                                                                        <input
                                                                            type="number"
                                                                            className="form-control form-control-sm text-center mx-2"
                                                                            style={{
                                                                                width:
                                                                                    "70px",
                                                                            }}
                                                                            min={
                                                                                minimum
                                                                            }
                                                                            step="1"
                                                                            value={
                                                                                quantity
                                                                            }
                                                                            aria-label={`Quantity for ${product.name}`}
                                                                            onChange={(
                                                                                event
                                                                            ) =>
                                                                                updateQuantity(
                                                                                    product,
                                                                                    event
                                                                                        .target
                                                                                        .value
                                                                                )
                                                                            }
                                                                        />

                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-secondary btn-sm"
                                                                            aria-label={`Increase quantity for ${product.name}`}
                                                                            onClick={() =>
                                                                                incrementQuantity(
                                                                                    product
                                                                                )
                                                                            }
                                                                        >
                                                                            +
                                                                        </button>
                                                                    </div>
                                                                </td>

                                                                <td>
                                                                    <div className="d-flex flex-wrap justify-content-center gap-2">
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-outline-dark btn-sm"
                                                                            onClick={() =>
                                                                                openProductDetail(
                                                                                    product
                                                                                )
                                                                            }
                                                                        >
                                                                            Details
                                                                        </button>

                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-dark btn-sm"
                                                                            onClick={() =>
                                                                                handleAddToCart(
                                                                                    product
                                                                                )
                                                                            }
                                                                        >
                                                                            Add
                                                                            to
                                                                            Cart
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : null}
                            </div>

                            {/* Wholesale terms */}
                            <div className="col-lg-3 p-t-30 p-t-0-lg">
                                <aside
                                    className="bor10 p-lr-25 p-t-30 p-b-30"
                                    style={{
                                        position:
                                            "sticky",

                                        top:
                                            "110px",
                                    }}
                                >
                                    <h2 className="mtext-110 cl2 p-b-20">
                                        Wholesale Terms
                                    </h2>

                                    <ul
                                        className="stext-102 cl6"
                                        style={{
                                            paddingLeft:
                                                "20px",

                                            lineHeight:
                                                "1.75",
                                        }}
                                    >
                                        <li className="p-b-10">
                                            Initial wholesale
                                            order minimum:{" "}
                                            <strong>
                                                {
                                                    minimumOrderUnits
                                                }{" "}
                                                units
                                            </strong>
                                        </li>

                                        <li className="p-b-10">
                                            Minimum quantity
                                            per SKU:{" "}
                                            <strong>
                                                6 units
                                            </strong>
                                        </li>

                                        <li className="p-b-10">
                                            Reorder minimum:{" "}
                                            <strong>
                                                12 units
                                            </strong>
                                        </li>

                                        <li className="p-b-10">
                                            Free shipping on
                                            wholesale orders
                                            of{" "}
                                            <strong>
                                                $500 or more
                                            </strong>
                                        </li>

                                        <li>
                                            Pricing subject
                                            to change without
                                            notice
                                        </li>
                                    </ul>

                                    <hr className="m-t-25 m-b-20" />

                                    <p className="stext-107 cl6">
                                        Order minimums,
                                        account status, and
                                        wholesale pricing are
                                        verified during
                                        checkout.
                                    </p>

                                    <button
                                        type="button"
                                        className="btn btn-dark w-100 m-t-25"
                                        onClick={() =>
                                            navigate(
                                                "/cart"
                                            )
                                        }
                                    >
                                        View Cart
                                    </button>
                                </aside>
                            </div>
                        </div>
                    </div>
                </section>
            </Layout>
        </div>
    );
};


export default WholesaleCatalog;